import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { PageTransition } from "@/components/PageTransition";
import { SearchBar } from "@/components/SearchBar";
import { AlphabeticalIndex } from "@/components/AlphabeticalIndex";
import { TermCard } from "@/components/TermCard";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTerms } from "@/hooks/useTerms";
import { Book, ChevronLeft, ChevronRight, Filter, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import logo from "@/assets/logo.png";

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

const BROWSE_GRID_CLASS =
  "grid gap-3 md:gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5";

const Browse = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const letter = searchParams.get("letter") || undefined;

  const { data: terms = [], isLoading, error, refetch } = useTerms({ letter });

  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE_OPTIONS[0]);
  const [page, setPage] = useState(1);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(terms.length / pageSize)),
    [terms.length, pageSize]
  );

  const safePage = Math.min(Math.max(1, page), totalPages);

  useEffect(() => {
    setPage(1);
  }, [letter]);

  useEffect(() => {
    setPage(1);
  }, [pageSize]);

  useEffect(() => {
    setPage(p => Math.min(p, totalPages));
  }, [totalPages]);

  const paginatedTerms = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return terms.slice(start, start + pageSize);
  }, [terms, safePage, pageSize]);

  const rangeStart = terms.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const rangeEnd = Math.min(safePage * pageSize, terms.length);

  const skeletonCount = Math.min(pageSize, 15);

  return (
    <PageTransition>
      <div className="min-h-screen">
        {/* Hero Header */}
        <section className="relative bg-gradient-to-br from-[hsl(220,40%,13%)] via-[hsl(220,38%,18%)] to-[hsl(218,35%,22%)]">
          {/* Animated Background */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="bg-primary/20 float absolute -right-20 -top-20 h-60 w-60 rounded-full blur-3xl" />
            <div className="bg-gold/15 float-delayed absolute -bottom-20 -left-20 h-80 w-80 rounded-full blur-3xl" />
          </div>

          {/* Decorative Pattern */}
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 50'%3E%3Ccircle cx='10' cy='10' r='1' fill='%23fff'/%3E%3Ccircle cx='30' cy='15' r='1' fill='%23fff'/%3E%3Ccircle cx='50' cy='8' r='1' fill='%23fff'/%3E%3Ccircle cx='70' cy='20' r='1' fill='%23fff'/%3E%3Ccircle cx='90' cy='12' r='1' fill='%23fff'/%3E%3C/svg%3E")`,
              backgroundSize: "150px 75px",
            }}
          />

          <div className="container relative py-5 md:py-12">
            <div className="flex flex-col items-center text-center">
              <div className="animate-fade-in-up relative mb-6">
                <div className="bg-primary/20 absolute inset-0 scale-150 rounded-full blur-xl" />
                <img
                  src={logo}
                  alt="Jain Lexicon"
                  className="relative h-16 w-auto object-contain"
                />
              </div>
              <h1
                className="animate-fade-in-up mb-3 font-serif text-3xl font-bold text-white md:text-5xl"
                style={{ animationDelay: "0.1s" }}
              >
                {t("nav.browse")}
              </h1>
              <p
                className="animate-fade-in-up md:mb-8 mb-4 max-w-xl text-base text-white/70 md:text-lg"
                style={{ animationDelay: "0.2s" }}
              >
                {t("browse.subtitle")}
              </p>
              <div
                className="animate-fade-in-up relative z-50 w-full max-w-lg"
                style={{ animationDelay: "0.3s" }}
              >
                <SearchBar variant="hero" />
              </div>
            </div>
          </div>

          {/* Bottom Wave */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg
              viewBox="0 0 1440 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="w-full"
            >
              <path
                d="M0 40V20C360 35 720 5 1080 20C1260 27.5 1350 32.5 1440 35V40H0Z"
                fill="white"
              />
            </svg>
          </div>
        </section>

        {/* Alphabetical Index */}
        <section className="from-background to-secondary/20 bg-gradient-to-b py-5 md:py-8">
          <div className="container">
            <AlphabeticalIndex />
          </div>
        </section>

        {/* Terms Grid */}
        <section className="py-5 md:py-12">
          <div className="container">
            {letter && (
              <div className="bg-primary/5 border-primary/10 md:mb-8 mb-4 flex flex-wrap items-center gap-3 rounded-xl border p-4">
                <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-full">
                  <Filter className="text-primary h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-muted-foreground text-sm">
                    {t("browse.filteringByLetter")}
                  </p>
                  <p className="text-foreground break-words font-semibold">
                    {t("browse.termsStartingWith", { letter })}
                  </p>
                </div>
              </div>
            )}

            {isLoading ? (
              <div className={BROWSE_GRID_CLASS}>
                {[...Array(skeletonCount)].map((_, i) => (
                  <div
                    key={i}
                    className="from-secondary/50 to-secondary/30 h-48 animate-pulse rounded-xl bg-gradient-to-br"
                  />
                ))}
              </div>
            ) : error ? (
              <div className="bg-destructive/5 border-destructive/20 rounded-2xl border py-16 text-center">
                <div className="bg-destructive/10 mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full">
                  <Book className="text-destructive h-10 w-10" />
                </div>
                <h3 className="text-foreground mb-3 font-serif text-xl font-semibold">
                  {t("common.loadErrorTitle")}
                </h3>
                <p className="text-muted-foreground mx-auto mb-8 max-w-md">
                  {t("common.loadErrorBody")}
                </p>
                <Button
                  onClick={() => refetch()}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground btn-shimmer"
                >
                  {t("common.retry")}
                </Button>
              </div>
            ) : terms.length > 0 ? (
              <>
                <div className={BROWSE_GRID_CLASS}>
                  {paginatedTerms.map((term, i) => (
                    <TermCard
                      key={term.id}
                      id={term.id}
                      word={term.word}
                      meaningEnglish={term.meaning_english}
                      meaningHindi={term.meaning_hindi}
                      meaningGujarati={term.meaning_gujarati}
                      pronunciation={term.pronunciation}
                      index={i}
                    />
                  ))}
                </div>

                <div className="border-border/60 mt-10 flex flex-col items-stretch justify-between gap-4 border-t md:pt-8 pt-5 md:flex-row  md:items-center">
                    <div className="flex flex-wrap items-center gap-3">
                      <Label
                        htmlFor="browse-page-size"
                        className="text-muted-foreground shrink-0 text-sm whitespace-nowrap"
                      >
                        {t("browse.perPage")}
                      </Label>
                      {/* Native select: Radix Select uses RemoveScroll and shifts layout with sticky header. */}
                      <select
                        id="browse-page-size"
                        aria-label={t("browse.perPage")}
                        value={String(pageSize)}
                        onChange={e =>
                          setPageSize(
                            Number(e.target.value) as (typeof PAGE_SIZE_OPTIONS)[number]
                          )
                        }
                        className={cn(
                          "border-input bg-background ring-offset-background",
                          "focus-visible:ring-ring h-9 w-[4.75rem] shrink-0 rounded-md border px-2 text-sm",
                          "focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                        )}
                      >
                        {PAGE_SIZE_OPTIONS.map(n => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                      <p className="text-muted-foreground text-sm tabular-nums md:ml-2">
                        {t("browse.paginationRange", {
                          start: rangeStart,
                          end: rangeEnd,
                          total: terms.length,
                        })}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center md:justify-center gap-2 sm:justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-9 gap-1"
                        disabled={safePage <= 1}
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        {t("browse.paginationPrev")}
                      </Button>
                      <span className="text-muted-foreground px-1 text-sm tabular-nums">
                        {t("browse.paginationPageOf", {
                          current: safePage,
                          total: totalPages,
                        })}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-9 gap-1"
                        disabled={safePage >= totalPages}
                        onClick={() =>
                          setPage(p => Math.min(totalPages, p + 1))
                        }
                      >
                        {t("browse.paginationNext")}
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
              </>
            ) : (
              <div className="from-secondary/30 to-secondary/10 border-border/50 rounded-2xl border bg-gradient-to-br py-16 text-center">
                <div className="bg-muted mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full">
                  <Book className="text-muted-foreground h-10 w-10" />
                </div>
                <h3 className="text-foreground mb-3 font-serif text-xl font-semibold">
                  {letter
                    ? t("browse.noTermsLetter", { letter })
                    : t("browse.noTermsGeneric")}
                </h3>
                <p className="text-muted-foreground mx-auto mb-8 max-w-md">
                  {letter
                    ? t("browse.tryDifferentLetter")
                    : t("browse.emptyGeneric")}
                </p>
                <Button
                  onClick={() => navigate("/contribute")}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground btn-shimmer"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {t("common.addTerm")}
                </Button>
              </div>
            )}
          </div>
        </section>
      </div>
    </PageTransition>
  );
};

export default Browse;
